import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled, { keyframes } from "styled-components";
import gsap from "gsap";
import { CHARACTERS } from "../data/dialogue";
import { sketchPath } from "../config";

/* ==================================================================
 * DIALOGUE
 *
 * Reads a character from src/data/dialogue.js and walks its nodes.
 * Nothing here is specific to any one character — the Citadel woman
 * and the witch drop in by passing a different characterId.
 *
 * Tap advances a line. When the lines run out you get either choices,
 * a paid gate, or the end.
 *
 * The gate is a DEMO. Real version: Stripe Checkout → webhook → the
 * backend marks the clue unlocked against the session → only then does
 * the client get the next node. Never unlock paid content on a
 * frontend payment confirmation.
 * ================================================================== */

export default function Dialogue({ characterId = "townsman", copy, lang, onFinish }) {
  const character = CHARACTERS[characterId];
  const script = character[lang] || character.en;

  const [nodeId, setNodeId] = useState(script.start);
  const [lineIndex, setLineIndex] = useState(0);
  const [asked, setAsked] = useState([]);
  const [translated, setTranslated] = useState(false);
  const [sketchOk, setSketchOk] = useState(false);

  const rootRef = useRef(null);
  const lineRef = useRef(null);

  const node = script.nodes[nodeId];
  const lines = node.lines || [];
  const linesDone = lineIndex >= lines.length - 1;

  // Reset if the language is switched mid-conversation.
  useEffect(() => {
    setNodeId(script.start);
    setLineIndex(0);
    setAsked([]);
    setTranslated(false);
  }, [script]);

  // Use the real drawing if it's on disk, otherwise the sketch fallback.
  useEffect(() => {
    if (!node.sketch) return;
    setSketchOk(false);
    const img = new Image();
    img.onload = () => setSketchOk(true);
    img.onerror = () => setSketchOk(false);
    img.src = sketchPath(node.sketch);
  }, [node.sketch]);

  // Fade each line in as it appears.
  useEffect(() => {
    if (!lineRef.current) return;
    const ctx = gsap.context(() => {
      gsap.fromTo(
        lineRef.current,
        { opacity: 0, y: 12 },
        { opacity: 1, y: 0, duration: 0.55, ease: "power2.out" }
      );
    }, rootRef);
    return () => ctx.revert();
  }, [nodeId, lineIndex]);

  const advance = useCallback(() => {
    if (!linesDone) setLineIndex((i) => i + 1);
  }, [linesDone]);

  const choose = (choice) => {
    if (choice.once) setAsked((a) => (a.includes(choice.to) ? a : [...a, choice.to]));
    setNodeId(choice.to);
    setLineIndex(0);
  };

  // Choices are hidden once used, and gated options stay hidden until
  // the player has actually asked something.
  const visibleChoices = useMemo(() => {
    if (!node.choices) return [];
    return node.choices.filter((c) => {
      if (c.once && asked.includes(c.to)) return false;
      if (c.requires && asked.length < c.requires) return false;
      return true;
    });
  }, [node, asked]);

  const showChoices = linesDone && visibleChoices.length > 0 && !node.gate;
  const showGate = linesDone && !!node.gate;
  const showEnd = linesDone && node.end;
  const tappable = !linesDone;

  const buy = () => {
    setTranslated(false);
    setNodeId(node.gate.to);
    setLineIndex(0);
  };

  return (
    <Root ref={rootRef}>
      {node.sketch ? (
        <SketchWrap>
          <SketchPaper>
            {sketchOk ? (
              <SketchImg src={sketchPath(node.sketch)} alt="" />
            ) : (
              <SketchFallback />
            )}
          </SketchPaper>
        </SketchWrap>
      ) : (
        <Portrait>
          <PortraitFrame>
            <Silhouette viewBox="0 0 100 100" aria-hidden="true">
              <circle cx="50" cy="36" r="17" />
              <path d="M14 100c0-22 16-34 36-34s36 12 36 34z" />
            </Silhouette>
          </PortraitFrame>
          <Name>{script.name}</Name>
          <Role>{script.role}</Role>
        </Portrait>
      )}

      <Panel onClick={tappable ? advance : undefined} $tappable={tappable}>
        <Speech ref={lineRef}>{lines[lineIndex]}</Speech>

        {tappable && <TapOn>{copy.continue}</TapOn>}

        {showChoices && (
          <Choices>
            {visibleChoices.map((c) => (
              <Choice key={c.to + c.text} onClick={() => choose(c)}>
                {c.text}
              </Choice>
            ))}
          </Choices>
        )}

        {showGate && (
          <GateBlock>
            <Francs>
              {node.gate.francs} {copy.francs}
            </Francs>

            {translated ? (
              <Converted>
                <ConvertArrow aria-hidden="true">↓</ConvertArrow>
                <ConvertLabel>{copy.todayApprox}</ConvertLabel>
                <ConvertValue>{node.gate.price}</ConvertValue>
              </Converted>
            ) : (
              <TranslateButton onClick={() => setTranslated(true)}>
                {copy.translateCta}
              </TranslateButton>
            )}

            <GateButton onClick={buy}>
              {copy.payCta} — {node.gate.price}
            </GateButton>
            <DemoNote>{copy.demoPayNote}</DemoNote>
          </GateBlock>
        )}

        {showEnd && (
          <Choices>
            <Choice onClick={onFinish}>{copy.continue}</Choice>
          </Choices>
        )}
      </Panel>
    </Root>
  );
}

/* ------------------------------------------------------------------ *
 * SKETCH FALLBACK
 * A rough pencil drawing of a box, in the character's own hand. Gets
 * replaced the moment a real drawing lands in public/media/clues/.
 * ------------------------------------------------------------------ */

function SketchFallback() {
  return (
    <FallbackSvg viewBox="0 0 200 170" aria-hidden="true">
      <g
        fill="none"
        stroke="#4a341c"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        {/* wall behind */}
        <path d="M14 118 L186 112" strokeWidth="1" opacity="0.45" />
        <path d="M20 128 L182 122" strokeWidth="0.8" opacity="0.3" />

        {/* box body, drawn slightly off-square on purpose */}
        <path d="M62 72 L138 68 L141 118 L64 122 Z" />
        {/* lid */}
        <path d="M62 72 L74 56 L148 53 L138 68" />
        <path d="M148 53 L151 100 L141 118" opacity="0.8" />
        {/* lid seam */}
        <path d="M63 84 L139 80" strokeWidth="1" opacity="0.6" />
        {/* clasp */}
        <path d="M96 78 L104 78 L104 92 L96 92 Z" />
        <circle cx="100" cy="85" r="2.6" />

        {/* hatching */}
        <path d="M70 96 L78 90 M78 104 L90 94 M86 112 L100 100" opacity="0.35" strokeWidth="0.9" />

        {/* arrow */}
        <path d="M34 44 Q48 60 60 68" opacity="0.7" />
        <path d="M56 58 L60 68 L50 68" opacity="0.7" />

        {/* mark */}
        <path d="M158 132 L172 146 M172 132 L158 146" opacity="0.75" />
      </g>
    </FallbackSvg>
  );
}

/* ------------------------------------------------------------------ */

const Root = styled.div`
  position: fixed;
  inset: 0;
  z-index: 30;
  background: radial-gradient(90% 60% at 50% 22%, #221c13 0%, var(--ink) 72%),
    var(--ink);
  display: flex;
  flex-direction: column;
  padding: calc(env(safe-area-inset-top) + 4vh) 7vw
    calc(env(safe-area-inset-bottom) + 3vh);
`;

/* --- portrait --- */

const Portrait = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.55rem;
  flex: none;
`;

const glow = keyframes`
  0%, 100% { box-shadow: 0 0 34px rgba(192, 139, 62, 0.1); }
  50%      { box-shadow: 0 0 46px rgba(192, 139, 62, 0.2); }
`;

/* No portrait asset yet. Swap this frame's contents for an <img> or a
   looping <video> when the character footage exists. */
const PortraitFrame = styled.div`
  width: min(30vw, 118px);
  aspect-ratio: 1;
  border-radius: 50%;
  overflow: hidden;
  display: grid;
  place-items: center;
  border: 1px solid rgba(192, 139, 62, 0.35);
  background: radial-gradient(circle at 50% 34%, #3a3122, #14110b 78%);
  animation: ${glow} 4.5s ease-in-out infinite;
`;

const Silhouette = styled.svg`
  width: 74%;
  height: 74%;
  fill: rgba(232, 220, 196, 0.16);
`;

const Name = styled.div`
  font-family: var(--font-display);
  font-size: 1.15rem;
  color: var(--parchment);
`;

const Role = styled.div`
  font-family: var(--font-body);
  font-size: 0.55rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--brass);
  opacity: 0.75;
`;

/* --- sketch --- */

const handOver = keyframes`
  from { opacity: 0; transform: translateY(-16px) rotate(-4deg); }
  to   { opacity: 1; transform: translateY(0) rotate(-1.6deg); }
`;

const SketchWrap = styled.div`
  flex: none;
  display: grid;
  place-items: center;
`;

const SketchPaper = styled.div`
  width: min(62vw, 236px);
  aspect-ratio: 7 / 6;
  padding: 0.6rem;
  overflow: hidden;
  animation: ${handOver} 0.75s ease-out both;
  filter: drop-shadow(0 16px 30px rgba(0, 0, 0, 0.7));

  background-color: #d9c8a4;
  background-image: radial-gradient(
      70% 60% at 26% 20%,
      rgba(255, 248, 228, 0.7),
      transparent 72%
    ),
    linear-gradient(162deg, #e2d3b2 0%, #d3bf97 100%);

  clip-path: polygon(
    1% 2%, 24% 0.6%, 52% 2.2%, 78% 0.5%, 99% 2%,
    98.4% 26%, 99.4% 52%, 98.6% 78%, 99% 98%,
    74% 99.4%, 48% 98%, 22% 99.5%, 1.4% 98.4%,
    2% 74%, 0.6% 50%, 1.8% 26%
  );
`;

const SketchImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  mix-blend-mode: multiply;
`;

const FallbackSvg = styled.svg`
  width: 100%;
  height: 100%;
  opacity: 0.82;
`;

/* --- panel --- */

const Panel = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  display: flex;
  flex-direction: column;
  justify-content: center;
  gap: 1.5rem;
  padding: 2rem 0 1rem;
  cursor: ${(p) => (p.$tappable ? "pointer" : "default")};
`;

const Speech = styled.p`
  margin: 0;
  font-family: var(--font-display);
  font-size: clamp(1.25rem, 5.4vw, 1.7rem);
  line-height: 1.44;
  color: var(--parchment);
  text-align: center;
  max-width: 28ch;
  align-self: center;
`;

const breathe = keyframes`
  0%, 100% { opacity: 0.28; }
  50%      { opacity: 0.7; }
`;

const TapOn = styled.div`
  text-align: center;
  font-family: var(--font-body);
  font-size: 0.6rem;
  letter-spacing: 0.32em;
  text-transform: uppercase;
  color: var(--parchment);
  animation: ${breathe} 2.4s ease-in-out infinite;
`;

const Choices = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.7rem;
  width: 100%;
  max-width: 26rem;
  align-self: center;
`;

const Choice = styled.button`
  width: 100%;
  padding: 0.95rem 1.2rem;
  text-align: left;
  border: 1px solid rgba(232, 220, 196, 0.2);
  font-family: var(--font-body);
  font-size: 0.92rem;
  line-height: 1.4;
  color: var(--parchment);
  transition: border-color 0.25s ease, background 0.25s ease;

  &:hover {
    border-color: rgba(192, 139, 62, 0.6);
    background: rgba(192, 139, 62, 0.07);
  }
`;

/* --- paid gate --- */

const GateBlock = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.9rem;
  align-self: center;
  text-align: center;
`;

const Francs = styled.div`
  font-family: var(--font-display);
  font-size: clamp(1.5rem, 7vw, 2rem);
  color: var(--brass);
  letter-spacing: 0.04em;
`;

const TranslateButton = styled.button`
  font-family: var(--font-body);
  font-size: 0.64rem;
  letter-spacing: 0.2em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.5;
  border-bottom: 1px dotted rgba(232, 220, 196, 0.4);
  padding-bottom: 3px;
  transition: opacity 0.25s ease;

  &:hover {
    opacity: 0.9;
  }
`;

const reveal = keyframes`
  from { opacity: 0; transform: translateY(-6px); }
  to   { opacity: 1; transform: translateY(0); }
`;

const Converted = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.3rem;
  animation: ${reveal} 0.5s ease-out both;
`;

const ConvertArrow = styled.div`
  color: var(--brass);
  opacity: 0.5;
  font-size: 0.9rem;
  line-height: 1;
`;

const ConvertLabel = styled.div`
  font-family: var(--font-body);
  font-size: 0.55rem;
  letter-spacing: 0.3em;
  text-transform: uppercase;
  color: var(--parchment);
  opacity: 0.42;
`;

const ConvertValue = styled.div`
  font-family: var(--font-display);
  font-size: 1.35rem;
  color: var(--parchment);
`;

const GateButton = styled.button`
  margin-top: 0.5rem;
  padding: 1rem 2.6rem;
  border: 1px solid rgba(192, 139, 62, 0.6);
  font-size: 0.7rem;
  letter-spacing: 0.26em;
  text-transform: uppercase;
  color: var(--brass);
  transition: background 0.3s ease;

  &:hover {
    background: rgba(192, 139, 62, 0.12);
  }
`;

const DemoNote = styled.div`
  font-family: ui-monospace, Menlo, monospace;
  font-size: 0.6rem;
  color: var(--parchment);
  opacity: 0.34;
`;
